import {
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Container,
  Stack,
  Typography,
  Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { CallStatus } from '@karehero/models';

import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { Icon } from '@/components/atoms/Icon/Icon';

export interface CallItem {
  id: string;
  title: string;
  when: string;
  withName: string;
  status: CallStatus;
}

export interface CallsListPageProps {
  calls: CallItem[];
  isLoading?: boolean;
  onBack?: () => void;
  onBookCall?: () => void;
  onViewCall: (call: CallItem) => void;
  isEmbedded?: boolean;
}

const SECTIONS: { id: CallStatus; label: string }[] = [
  { id: CallStatus.Upcoming, label: 'UPCOMING' },
  { id: CallStatus.Past, label: 'PAST' },
];

export const CallsListPage = ({
  calls,
  isLoading = false,
  onBack,
  onBookCall,
  onViewCall,
  isEmbedded = false,
}: CallsListPageProps) => {
  const hasCalls = calls.length > 0;

  const groupedCalls = SECTIONS.map((section) => ({
    ...section,
    items: calls.filter((c) => c.status === section.id),
  })).filter((section) => section.items.length > 0);

  const cta = !isLoading && onBookCall && (
    <Box
      sx={{
        position: isEmbedded ? 'absolute' : 'relative',
        left: isEmbedded ? 0 : undefined,
        right: isEmbedded ? 0 : undefined,
        bottom: isEmbedded ? 0 : undefined,
        py: 1.5,
        bgcolor: (theme) => alpha(theme.palette.background.default, 0.72),
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      }}
    >
      <Container maxWidth='sm'>
        <Button
          variant='contained'
          size='large'
          fullWidth
          aria-label='Book a call'
          onClick={onBookCall}
        >
          Book a call
        </Button>
      </Container>
    </Box>
  );

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          pt: 2,
          pb: isEmbedded && onBookCall ? '72px' : 0,
        }}
      >
        <Container maxWidth='sm'>
          <Stack spacing={2}>
            {isLoading && (
              <Stack
                sx={{
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <CircularProgress size={32} />
              </Stack>
            )}

            {!isLoading && hasCalls && (
              <Stack spacing={1}>
                {groupedCalls.map((section) => (
                  <Box key={section.id}>
                    <Typography
                      variant='overline'
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        mt: 1,
                        mb: 1,
                      }}
                    >
                      {section.label}
                    </Typography>
                    <Stack spacing={1.5}>
                      {section.items.map((call) => (
                        <Card
                          key={call.id}
                          variant='listItem'
                          onClick={() => onViewCall(call)}
                        >
                          <CardHeader
                            className='list-item'
                            title={call.title}
                            subheader={`${call.when} · with ${call.withName}`}
                            action={<Icon icon='chevron-right' />}
                            slotProps={{
                              title: { variant: 'h5' },
                            }}
                          />
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}

            {!isLoading && !hasCalls && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  minHeight: 300,
                }}
              >
                <PageHeader title='No calls yet' isCentered />
              </Box>
            )}
          </Stack>
        </Container>
      </Box>

      {cta}
    </Box>
  );

  if (isEmbedded) {
    return content;
  }

  return (
    <ModalPageTemplate onBack={onBack}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
          }}
        >
          <Container maxWidth='sm'>
            <PageHeader
              title='Calls'
              description='Calls with your KareHero advisor'
            />
          </Container>
          {content}
        </Box>
      </Box>
    </ModalPageTemplate>
  );
};

CallsListPage.displayName = 'CallsListPage';
export default CallsListPage;
